/**
 * Cross-reference parsing utilities
 *
 * Cross-references in card content use the syntax: [[L-ID]]
 * Accepts both formats: [[1/1a]] or [[1-1a]]
 * Internally normalizes to dash format (1-1a)
 */

import type { CrossReference } from '@/types'
import { isValidLid, normalizeLid } from './lid'

// Regex to match [[L-ID]] pattern
const CROSS_REF_REGEX = /\[\[([^\]]+)\]\]/g

/**
 * Extract all cross-references from content
 * Normalizes LIDs to dash format (accepts both / and - separators)
 */
export function extractCrossReferences(content: string): CrossReference[] {
  const references: CrossReference[] = []
  let match: RegExpExecArray | null

  // Reset regex lastIndex
  CROSS_REF_REGEX.lastIndex = 0

  while ((match = CROSS_REF_REGEX.exec(content)) !== null) {
    // Normalize to dash format (accepts both / and - separators)
    const targetLid = normalizeLid(match[1].trim())

    // Only include valid L-IDs
    if (isValidLid(targetLid)) {
      references.push({
        targetLid,
        position: match.index,
      })
    }
  }

  return references
}

/**
 * Get unique cross-referenced L-IDs from content
 */
export function getUniqueCrossReferences(content: string): string[] {
  const refs = extractCrossReferences(content)
  return [...new Set(refs.map((r) => r.targetLid))]
}

/**
 * Check if content contains a reference to a specific L-ID
 * Normalizes the lid parameter to dash format before comparison
 */
export function hasReferenceToLid(content: string, lid: string): boolean {
  const refs = extractCrossReferences(content)
  const normalizedLid = normalizeLid(lid)
  return refs.some((r) => r.targetLid === normalizedLid)
}

/**
 * Replace cross-references with custom format (for rendering)
 * Returns the content with references replaced by the result of the replacer function
 * LIDs are normalized to dash format before being passed to the replacer
 */
export function replaceCrossReferences(
  content: string,
  replacer: (lid: string, fullMatch: string) => string
): string {
  return content.replace(CROSS_REF_REGEX, (fullMatch, lid) => {
    const normalizedLid = normalizeLid(lid.trim())
    if (isValidLid(normalizedLid)) {
      return replacer(normalizedLid, fullMatch)
    }
    return fullMatch
  })
}

/**
 * Create a cross-reference string
 */
export function createCrossReference(lid: string): string {
  return `[[${lid}]]`
}

/**
 * Split content into segments with cross-references marked
 */
export interface ContentSegment {
  type: 'text' | 'crossref'
  content: string
  lid?: string // Only for crossref type
}

export function parseContentSegments(content: string): ContentSegment[] {
  const segments: ContentSegment[] = []
  let lastIndex = 0

  // Reset regex lastIndex
  CROSS_REF_REGEX.lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = CROSS_REF_REGEX.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: content.substring(lastIndex, match.index),
      })
    }

    // Normalize to dash format (accepts both / and - separators)
    const lid = normalizeLid(match[1].trim())
    if (isValidLid(lid)) {
      segments.push({
        type: 'crossref',
        content: match[0],
        lid,
      })
    } else {
      // Invalid L-ID, treat as text
      segments.push({
        type: 'text',
        content: match[0],
      })
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      content: content.substring(lastIndex),
    })
  }

  return segments
}
